import * as Tooltip from '@radix-ui/react-tooltip';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import Link from 'next/link';
import { slugify } from '../../../lib/map/slugify';
import { useViewport } from '@xyflow/react';
import { isLoadingNavAtom, contentAtom, mobileSidebar } from '@/jotaiStore';
import { useAtom } from 'jotai';
import getContentLink from '@/lib/map/getContentLink';
import { useParams } from 'next/navigation';

interface TooltipContentProps {
  title: string;
  description: string;
  mapSlug: string;
  labelText: string;
  labelColor?: string;
  number?: number;
  labelStyle?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
}

export const TooltipContent = ({
  title,
  description,
  mapSlug,
  labelText,
  labelColor = 'text-shades-text/85 opacity-100',
  number,
  labelStyle,
  side = 'top',
}: TooltipContentProps) => {
  const { x, y, zoom } = useViewport();
  const [showMobileSidebar, setShowMobileSidebar] = useAtom(mobileSidebar);
  const [, setIsLoadingNav] = useAtom(isLoadingNavAtom);
  const [, setContentData] = useAtom(contentAtom);
  const introLink = `/map/${mapSlug}?showIntro=true&x=${x}&y=${y}&zoom=${zoom}`;
  const { contentSlug } = useParams();

  const linkToUse = title === 'Overview' ? introLink : getContentLink(x, y, zoom, contentSlug as string, mapSlug, title);

  return (
    <Tooltip.Content
      className="min-w-[300px] bg-shades-baseLightest z-[1000] text-shades-textLight p-4 rounded-md max-w-sm"
      side={side}
    >
      <p
        className={`text-sm font-medium leading-4 tracking-wider ${labelColor}`}
        style={{ color: labelStyle }}
      >
        {labelText} {number && `${number}`}
      </p>
      <h3 className="text-shades-textLight text-[22px] font-medium mb-2">
        {title}
      </h3>
      <div className="text-shades-text opacity-80">{description}</div>
      <div className="flex justify-end mt-2">
        <Link
          href={linkToUse}
          className="flex items-center justify-center gap-0.5 pl-3 px-2 py-1 transition-colors duration-300 rounded-md bg-colors-green hover:bg-colors-greenDark text-shades-base font-semibold"
          onClick={() => {
            if (showMobileSidebar) setShowMobileSidebar(false);
            setIsLoadingNav(true);
            setContentData('');
          }}
        >
          View
          <ArrowRightIcon className="w-6 h-6" />
        </Link>
      </div>
      <Tooltip.Arrow className="fill-shades-surfaceDark" />
    </Tooltip.Content>
  );
};
